/**
 * FormLayoutField — pole „Układ formularza" w oknie edycji typu rekordu (typ dokumentu, typ zadania).
 *
 * Jedna lista wyboru z: opcją „domyślny layout kartoteki" (brak wskazania), układami formularza
 * kartoteki (z dopiskiem „(domyślny)") i — gdy zapisane wskazanie nie ma odpowiednika na liście
 * (układ usunięty / przeniesiony) — opcją „(wskazany layout niedostępny)", żeby lista nie
 * pokazywała fałszywie pustego wyboru, mimo że stan trzyma realne id. Gdy lista układów nie
 * wczytała się (`unavailable`), pole jest zablokowane z podpowiedzią.
 */

import { useTranslation } from 'react-i18next';

import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import type { FormLayoutOption } from '@/lib/record-types/form-layout';

export interface FormLayoutFieldProps {
  layouts: FormLayoutOption[];
  /** Lista układów nie wczytała się (np. 403 / błąd sieci) — pole zablokowane. */
  unavailable?: boolean;
  /** Wskazany układ; `null` = domyślny układ kartoteki. */
  value: string | null;
  onChange: (layoutId: string | null) => void;
  /** Komunikat błędu serwera pod polem. */
  error?: string;
  'data-testid'?: string;
}

export function FormLayoutField({
  layouts,
  unavailable = false,
  value,
  onChange,
  error,
  'data-testid': testId,
}: FormLayoutFieldProps) {
  const { t } = useTranslation();
  return (
    <FormField
      label={t('tenant.recordTypes.formLayout.layoutLabel')}
      hint={unavailable ? t('tenant.recordTypes.formLayout.unavailableHint') : undefined}
      error={error}
    >
      <Dropdown
        value={value ?? ''}
        onChange={(v) => onChange(v || null)}
        disabled={unavailable}
        data-testid={testId}
        options={[
          { value: '', label: t('tenant.recordTypes.formLayout.defaultOption') },
          ...layouts.map((l) => ({
            value: l.id,
            label: l.isDefault
              ? `${l.name} ${t('tenant.recordTypes.formLayout.defaultSuffix')}`
              : l.name,
          })),
          ...(value !== null && !layouts.some((l) => l.id === value)
            ? [{ value, label: t('tenant.recordTypes.formLayout.missing') }]
            : []),
        ]}
      />
    </FormField>
  );
}
